"use client";

import { useMemo, useState } from "react";
import { MinusIcon, PlusIcon, SearchIcon } from "@/components/icons/icons";
import type { Dictionary } from "@/i18n/dictionaries";

export function FaqExperience({ dict }: { dict: Dictionary }) {
  const [topic, setTopic] = useState<keyof Dictionary["faq"]["topics"]>("General");
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [query, setQuery] = useState("");

  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = dict.faq.items;
    if (!q) return base;
    return base.filter(
      (i) =>
        i.question.toLowerCase().includes(q) ||
        i.answer.toLowerCase().includes(q),
    );
  }, [query, dict.faq.items]);

  const topicEntries = Object.entries(dict.faq.topics) as Array<
    [keyof Dictionary["faq"]["topics"], string]
  >;

  return (
    <div className="flex flex-col gap-12">
      <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between lg:gap-8">
        <div className="max-w-[680px]">
          <h1 className="text-[40px] font-medium leading-[1.1] tracking-[-0.04em] text-neutral-950 sm:text-[52px] sm:leading-[64px]">
            {dict.faq.hero.headingLines[0]}
            <br />
            {dict.faq.hero.headingLines[1]}
          </h1>
          <p className="mt-4 max-w-[600px] text-base leading-6 tracking-tight text-neutral-500">
            {dict.faq.hero.description}
          </p>
        </div>
        <label className="relative flex w-full items-center sm:max-w-[320px]">
          <span className="sr-only">{dict.faq.hero.searchPlaceholder}</span>
          <SearchIcon className="pointer-events-none absolute right-4 size-5 text-neutral-400" />
          <input
            type="search"
            placeholder={dict.faq.hero.searchPlaceholder}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpenIndex(0);
            }}
            className="h-11 w-full rounded-lg border border-neutral-200 bg-white pl-4 pr-11 text-sm tracking-tight text-neutral-950 shadow-[var(--shadow-xs)] placeholder:text-neutral-400 focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand-ring"
          />
        </label>
      </div>

      <div className="flex flex-col gap-12 lg:flex-row lg:gap-20">
        <div className="flex w-full flex-col gap-4 lg:w-[250px] lg:flex-shrink-0">
          <h2 className="text-center text-[18px] font-semibold tracking-tight text-neutral-950 lg:text-left">
            {dict.faq.intro.eyebrow}
          </h2>
          <ul className="flex flex-row gap-2 lg:flex-col lg:gap-0">
            {topicEntries.map(([key, label]) => {
              const active = key === topic;
              return (
                <li key={key}>
                  <button
                    type="button"
                    onClick={() => {
                      setTopic(key);
                      setOpenIndex(0);
                    }}
                    className={`flex h-11 w-full items-center justify-start rounded-lg px-4 text-base font-medium tracking-tight transition-colors ${
                      active
                        ? "bg-brand-soft text-brand"
                        : "text-neutral-500 hover:text-neutral-950"
                    }`}
                  >
                    {label}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="flex flex-1 flex-col gap-8">
          <h3 className="text-2xl font-medium tracking-tight text-neutral-950">
            {dict.faq.topics[topic]}
          </h3>
          {items.length === 0 ? (
            <p className="rounded-xl border border-dashed border-greyscale-100 bg-white p-6 text-base text-neutral-500">
              &ldquo;{query}&rdquo;
            </p>
          ) : (
            <ul className="flex flex-col gap-4">
              {items.map((item, i) => {
                const open = i === openIndex;
                return (
                  <li
                    key={item.question}
                    className="overflow-hidden rounded-xl border border-greyscale-100 bg-white"
                  >
                    <button
                      type="button"
                      aria-expanded={open}
                      onClick={() => setOpenIndex(open ? null : i)}
                      className="flex w-full items-center justify-between gap-6 p-6 text-left"
                    >
                      <span className="flex-1 text-[20px] font-medium leading-8 tracking-[-0.04em] text-greyscale-900 sm:text-[24px]">
                        {item.question}
                      </span>
                      {open ? (
                        <MinusIcon className="size-6 flex-shrink-0 text-greyscale-900" />
                      ) : (
                        <PlusIcon className="size-6 flex-shrink-0 text-greyscale-900" />
                      )}
                    </button>
                    {open ? (
                      <div className="px-6 pb-6">
                        <p className="text-base leading-6 tracking-tight text-greyscale-500">
                          {item.answer}
                        </p>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
